"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Stethoscope } from "lucide-react";
import posthog from "posthog-js";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onLogin(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(null);

    posthog.capture("login_initiated", { method: "email" });

    const supabase = createClient();
    const { error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    setLoading(false);

    if (authError) {
      setError(authError.message);
      posthog.capture("login_error", { method: "email", error: authError.message });
      return;
    }

    posthog.capture("login_success", { method: "email" });
    router.replace("/scan");
    router.refresh();
  }

  async function onGoogleLogin() {
    setLoading(true);
    setError(null);

    posthog.capture("google_login_initiated");

    const supabase = createClient();
    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=/scan`,
      },
    });

    if (authError) {
      setError(authError.message);
      posthog.capture("google_login_error", { error: authError.message });
      setLoading(false);
    }
  }

  return (
    <div className="flex h-screen w-full overflow-hidden" style={{ background: "var(--background)" }}>
      {/* Left — decorative panel */}
      <div
        className="relative hidden lg:flex lg:w-1/2 flex-col justify-between p-10 overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0c4a6e 100%)",
        }}
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.08) 1px,transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="relative flex items-center gap-2.5 z-10">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: "var(--accent)" }}>
            <Stethoscope size={18} className="text-white" />
          </div>
          <span className="text-lg font-semibold text-white">MedScan Extract</span>
        </div>
        <div className="relative z-10">
          <h2 className="text-3xl font-bold text-white leading-snug">
            Extração inteligente
            <br />
            de dados cirúrgicos
          </h2>
          <p className="mt-3 text-base text-blue-200/80">
            Fotografe a descrição cirúrgica e deixe a IA extrair todos os dados em segundos.
          </p>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex w-full lg:w-1/2 flex-col items-center justify-center px-6 py-10">
        <div className="w-full max-w-sm space-y-6">
          <div className="flex items-center gap-2.5 lg:hidden">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: "var(--accent)" }}>
              <Stethoscope size={15} className="text-white" />
            </div>
            <span className="text-sm font-semibold" style={{ color: "var(--text)" }}>MedScan Extract</span>
          </div>

          <div>
            <h1 className="text-2xl font-bold" style={{ color: "var(--text)" }}>Bem-vindo de volta</h1>
            <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>Entre para acessar suas capturas.</p>
          </div>

          <form onSubmit={onLogin} className="space-y-4">
            <Input
              id="email"
              label="E-mail"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="medico@hospital.com"
            />
            <Input
              id="password"
              label="Senha"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />

            {error && (
              <p className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-400">
                {error}
              </p>
            )}

            <Button type="submit" size="lg" disabled={loading} className="w-full">
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1" style={{ background: "var(--border)" }} />
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>ou</span>
            <div className="h-px flex-1" style={{ background: "var(--border)" }} />
          </div>

          <Button type="button" variant="outline" size="lg" disabled={loading} className="w-full" onClick={onGoogleLogin}>
            <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Entrar com Google
          </Button>

          <p className="text-center text-sm" style={{ color: "var(--text-muted)" }}>
            Não tem conta?{" "}
            <Link href="/register" className="font-medium hover:text-zinc-100 transition-colors" style={{ color: "var(--text)" }}>
              Criar conta
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
